<template>
	<InputRestricted
		ref="inputComponent"
		v-bind="{ ...$attrs, ...$props }"
		v-model="inputValue"
		:allowedFormatChecker="checker"
		:toolTipText="toolTipTextComputed"
		:showTooltipOnFocus="showTooltipOnFocus"
		:correctOnBlur="correctOnBlur"
		:allowedSymbolsRegex="allowedSymbolsRegex"
	/>
</template>

<script>
import { computed, ref, watch, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVModel } from '@vueuse/core';

import InputRestricted from '@/components/common/InputRestricted.vue';

export default {
	components: {
		InputRestricted,
	},
	emits: ['update:modelValue'],
	props: {
		allowPoint: {
			type: Boolean,
			default: true,
		},
		range: {
			type: String,
			default: 'all',
			validator(value) {
				return ['all', 'positive', 'negative', 'non-positive', 'non-negative', 'port', 'latitude', 'percents', 'invalid'].includes(value);
			},
		},
		// работает только в режиме all
		minValue: {
			type: Number,
			required: false,
		},
		// работает только в режиме all
		maxValue: {
			type: Number,
			required: false,
		},
		// String, Number or Null
		modelValue: {
			required: true,
		},
		showTooltipOnFocus: {
			type: Boolean,
			default: false,
		},
		toolTipText: {
			type: String,
			default: null,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const inputComponent = ref(null);

		const allowPointRef = toRef(props, 'allowPoint');
		const rangeRef = toRef(props, 'range');
		const minValueRef = toRef(props, 'minValue');
		const maxValueRef = toRef(props, 'maxValue');
		const toolTipTextRef = toRef(props, 'toolTipText');

		const inputValue = useVModel(props, 'modelValue', emit);

		const correctOnBlur = computed(() => rangeRef.value === 'all' && (minValueRef.value != null || maxValueRef.value != null));

		const allowMinus = computed(() => {
			if (['negative', 'non-positive', 'latitude'].includes(rangeRef.value)) {
				return true;
			}
			if (rangeRef.value === 'all') {
				if (minValueRef.value != null) {
					return minValueRef.value < 0;
				}
				return true;
			}
			return false;
		});

		const buildBoundsHint = () => {
			const parts = [];
			if (minValueRef.value != null) {
				parts.push(t('validators.inputDecimal.minValue', [minValueRef.value]));
			}
			if (maxValueRef.value != null) {
				parts.push(t('validators.inputDecimal.maxValue', [maxValueRef.value]));
			}
			return parts;
		};

		const toolTipTextComputed = computed(() => {
			if (toolTipTextRef.value != null) {
				return toolTipTextRef.value;
			}
			if (rangeRef.value === 'invalid') {
				return t('validators.inputDecimal.invalid');
			}
			const parts = [t('validators.inputDecimal.' + (allowPointRef.value ? 'float' : 'int'))];
			if (rangeRef.value !== 'all') {
				parts.push(t('validators.inputDecimal.' + rangeRef.value));
			} else {
				parts.push(...buildBoundsHint());
			}
			return parts.join(' ');
		});

		const rangeValidators = {
			positive: n => n > 0,
			negative: n => n < 0,
			'non-positive': n => n <= 0,
			'non-negative': n => n >= 0,
			port: n => n >= 1 && n <= 65535,
			latitude: n => n >= -90 && n <= 90,
			percents: n => n >= 0 && n <= 100,
			invalid: () => false,
		};

		// режим 'all': проверка границ min/max
		const isWithinBounds = numberValue => (
			(minValueRef.value == null || numberValue >= minValueRef.value)
			&& (maxValueRef.value == null || numberValue <= maxValueRef.value)
		);

		const isValidNumberFormat = (value, regexpStr) => !isNaN(+value) && RegExp(regexpStr).test(value);

		const validate = (value, regexpStr) => {
			if (value === '') {
				return true;
			}
			if (value === '-' && allowMinus.value) {
				return true;
			}
			if (!isValidNumberFormat(value, regexpStr)) {
				return false;
			}
			const rangeValidator = rangeValidators[rangeRef.value] || isWithinBounds;
			return rangeValidator(+value);
		};

		const allowedSymbolsRegex = computed(() => {
			let regexpStr = '';
			if (allowMinus.value) {
				regexpStr += '-';
			}
			regexpStr += '0-9';
			if (allowPointRef.value) {
				regexpStr += '.,';
			}
			return regexpStr;
		});

		const checker = (value) => {
			let regexpStr = '^';
			if (allowMinus.value) {
				regexpStr += '-?';
			}
			regexpStr += '\\d*';
			if (allowPointRef.value) {
				regexpStr += '([.,]\\d*)?';
			}
			regexpStr += '$';
			let val = String(value).replace(',', '.');
			return {
				valid: validate(val, regexpStr),
				value: val,
			}
		};

		watch([minValueRef, maxValueRef, rangeRef], () => {
			if (inputComponent.value) {
				inputComponent.value.validateValue(inputValue.value);
			}
		});

		return {
			inputComponent,
			inputValue,
			correctOnBlur,
			toolTipTextComputed,
			allowedSymbolsRegex,
			checker,
		};
	},
}
</script>
